// AiContextBar + AiContextChip — the ONE rendering of "what the AI can see"
// (O1, tickets/opus.md — design decided 2026-08-16, built by brand).
//
// The bar feeds ITSELF: it calls buildAiAwareness(root, composerState) — the
// derived set beside buildPageContext in workspace/functions/pageContext.js, so
// the chips can never claim more than the wire carries. Surfaces feed it
// nothing; a surface participates by writing its own state key, which the
// resolver's ladder already reads. Adding a surface = one branch in
// pageContext.js — never a new chip row.
//
// `composerState` = the state this bar inherits from its host composer (the
// draft bag: draft/files/refs/images). Two composers can be live at once, so
// the bag is never mirrored to root — the bar simply passes `s`.
//
// It REPLACES four near-copy renderings: canvas SelectionChips/CvSelectionChip,
// PromptGallery.AttachmentChips/PromptAttachmentChip, the sidebar
// PickedContextChip, and ChatMessage.PickedAttached. One grammar for all of
// them: borderless pills on the house washes (design-direction: fill contrast,
// no hairlines), the `sent` truth as a quiet caption suffix, one small remove
// button that dispatches the item's descriptor via el.call — never a handler
// of its own.
//
// Reactivity: canvas selection is a plain in-place mutation; `_cvSelectionTick`
// is the ONLY bridge — subscribed below (plus the other flat inputs). Reads of
// s.refs/s.files/s.images inside the factories track the composer bag.

// The tag is a polyglot KEY per delivery state, resolved at the render site —
// `structured` is the silent default and carries no tag at all.
const SENT_TAG = {
  structured: '',
  'text-only': 'ai.contextBar.tag.textOnly',
  truncated: 'ai.contextBar.tag.truncated',
  dropped: 'ai.contextBar.tag.dropped'
}

export const AiContextChip = {
  // A chip that carries a `refKey` is THE ALTERABLE REFERENCE (the open
  // record): a real button that toggles the record in and out of the prompt
  // (root._aiRefMutedFor — see pageContext.js). Everything else stays an
  // informational span.
  tag: (el, s) => (s.refKey ? 'button' : 'span'),
  type: (el, s) => (s.refKey ? 'button' : undefined),
  cursor: (el, s) => (s.refKey ? 'pointer' : undefined),
  borderWidth: '0',
  flow: 'x',
  align: 'center start',
  gap: 'Y',
  // The glyph plate (spec §7.2) sits close to the pill's start; a chip with no glyph keeps the even inset.
  // Single-value props only — a reactive multi-part spacing value emits nothing on this runtime.
  paddingBlock: 'X',
  paddingRight: 'Z',
  paddingLeft: (el, s) => (s.icon ? 'X' : 'Z'),
  round: 'radiusPill',
  maxWidth: 'F',
  // the status mark's containing block (SentMark below)
  position: 'relative',
  minWidth: '0',
  fontSize: 'Y2',
  // Implicit focus = the quiet `overlay` wash; an item the user HANDED over
  // (attached) = the `selected` wash. Same pill, one step of emphasis apart.
  // A toggleable reference answers hover with the `hover` wash so it reads
  // as pressable; the other chips never do.
  background: (el, s) => (s.tier === 'attached' ? 'selected' : 'overlay'),
  ':hover': (el, s) => (s.refKey ? { background: 'hover' } : {}),
  // THE ONE STATE LOGIC (dropdowns spec §5): only the chip that is a real button (the alterable reference)
  // answers the pointer — its plate INVERTS; keyboard focus inverts it too and draws the house ring.
  '&[type="button"]:hover [data-glyph-plate]': { background: 'highlight', color: 'title-reversed' },
  '&:focus-visible [data-glyph-plate]': { background: 'highlight', color: 'title-reversed' },
  ':focus-visible': { shadow: 'focusRing' },
  // Dropped items read as the quiet ink — the label stays legible, the state
  // is unmistakable, no strike-through noise. A muted reference IS a dropped
  // item, so the existing truth grammar covers the toggle for free.
  // D-2 (2026-09-24): this text can sit on a washed dark row, where `inactive`
  // reads 4.29:1 on `hover` and 3.33:1 on `selected` — both under 4.5 — so the
  // quiet ink here is `caption`. (A chip washes `hover` on hover and `selected` when attached.)
  color: (el, s) => (s.sent === 'dropped' ? 'caption' : 'title'),
  // `s.title` is the item's own name (tenant content) and passes through
  // `translate()` unchanged; the sentence around it comes from a key with a
  // {name} hole, so a translation orders the two parts its own way.
  title: (el, s) => {
    const name = el.call('polyglot', s.title || '')
    if (s.refKey) {
      const key = s.muted ? 'ai.contextBar.chip.mutedTitle' : 'ai.contextBar.chip.refTitle'
      return el.call('polyglot', key).replace('{name}', name)
    }
    const tag = SENT_TAG[s.sent]
    return tag ? name + ' — ' + el.call('polyglot', tag) : name
  },
  onClick: (e, el, s) => {
    if (!s.refKey) return
    e.preventDefault()
    e.stopPropagation()
    if (s.root && s.root.update) {
      s.root.update({ _aiRefMutedFor: s.muted ? null : s.refKey })
    }
  },

  // The glyph on a borderless round plate (spec §4 chip: `glyphPlateXs`); the holder restyles it through
  // `data-glyph-plate` (the MenuPanelItem keycap pattern).
  Plate: {
    if: (el, s) => !!s.icon,
    dataGlyphPlate: true,
    ariaHidden: 'true',
    display: 'flex',
    align: 'center center',
    flexShrink: '0',
    boxSize: 'glyphPlateXs',
    round: 'radiusPill',
    color: 'currentColor',
    transition: 'background-color A defaultBezier, color A defaultBezier',
    Icon: { name: (el, s) => s.icon, boxSize: 'iconSm' }
  },

  // The visible name. Resolved through polyglot exactly like the `title:`
  // tooltip above, and for the same reason SentTag had to be: an item's title
  // can BE a bundle key — an attached ref with no name of its own carries its
  // KIND as `ai.sources.kind.<type>` (workspace/functions/pageContext.js) —
  // and an unresolved key paints itself onto the chip. Tenant content is
  // unaffected: `translate()` returns its input on a miss, which is what makes
  // one call correct for both.
  Label: {
    tag: 'span',
    text: (el, s) => el.call('polyglot', s.title || ''),
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: '0'
  },

  // The fourth state made visible: text-only / partial / not sent. Structured
  // items carry no suffix — the absence is the "fully sent" signal.
  //
  // SENT_TAG holds polyglot KEYS, so the text has to go through polyglot the
  // same way `title:` above already does. Without that this span painted the
  // literal key — `ai.contextBar.tag.textOnly` — next to the chip's label,
  // caught on screen during the VaultOS v2 integration run (2026-09-07). The
  // `title:` attribute translated correctly the whole time, which is exactly
  // why nobody saw it: the tooltip read right and only the visible chip was
  // wrong.
  //
  // SentTag DEFECT (dropdowns spec §7.2, the ka frame): the visible word ("ნაწილობრივ") starved the label to
  // "Q3 …". The word leaves the visible row (approved 2026-10-01): SentMark shows the state in the chip's
  // corner, the word stays in the chip's `title` above AND in the accessibility tree — the sr-only idiom
  // (PageSrTitle: the 1px clip box, the one raw-px exception; never display/visibility/opacity, which drop it).
  SentMark: {
    if: (el, s) => !!SENT_TAG[s.sent],
    ariaHidden: 'true',
    position: 'absolute',
    top: '-X',
    right: '-X',
    boxSize: 'glyphDot',
    round: 'radiusPill',
    background: 'caption',
    pointerEvents: 'none'
  },
  SentTag: {
    tag: 'span',
    if: (el, s) => !!SENT_TAG[s.sent],
    text: (el, s) => el.call('polyglot', SENT_TAG[s.sent]),
    position: 'absolute',
    width: '1px',
    height: '1px',
    margin: '-1px',
    padding: '0',
    border: '0',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap'
  },

  RemoveBtn: {
    tag: 'button',
    type: 'button',
    if: (el, s) => !!(s.removeVia && s.removeVia.fn),
    'aria-label': (el, s) =>
      el
        .call('polyglot', 'ai.contextBar.chip.remove')
        .replace('{name}', el.call('polyglot', s.title || '')),
    flexShrink: '0',
    padding: 'X',
    margin: '0 -X 0 0',
    round: '50%',
    background: 'transparent',
    borderWidth: '0',
    color: 'caption',
    cursor: 'pointer',
    lineHeight: '0',
    ':hover': { background: 'hover', color: 'title' },
    Icon: { name: 'x', boxSize: 'iconSm' },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const via = s.removeVia
      if (!via || !via.fn) return
      try {
        el.call(via.fn, via.arg)
      } catch (_) {
        /* unregistered on this surface — the chip stays, nothing throws */
      }
    }
  }
}

export const AiContextBar = {
  // Every flat root input the resolver reads. `_cvSelectionTick` is mandatory
  // (see header); the record keys make an opened ticket/doc/person appear
  // without a composer keystroke.
  subscribeTo: [
    '_cvSelectionTick',
    'pickedElements',
    'currentPath',
    '_canvasSlot',
    // The split's second pane can hold the note editor too (pageContext
    // editorOnScreen): its close must drop the note chip at once.
    '_canvasSlotSecond',
    'workspace',
    'org',
    'selectedTicket',
    'selectedEvent',
    'showEventDetail',
    'selectedDocument',
    'profileEmail',
    'selectedPerson',
    '_selectedAgent',
    '_selectedTeamMember',
    '_createAgentOpen',
    '_createMemberOpen',
    'selectedAnnouncement',
    // An open note lives on `editorDoc` (the editor's write key); the open
    // canvas project's name lives on `data` (loads after the route) — both
    // feed detectOpenRecord (AI-CONTEXT-AWARENESS-O1-1 live pass).
    'editorDoc',
    'data',
    '_promptFocused',
    // The alterable-reference toggle — the chip writes it, the whole bar
    // re-derives (sent truth, mute rendering) on the flip.
    '_aiRefMutedFor',
    // STRUCTURED attachments (spec §4.1) — `root.aiAttached` is written by
    // attachAiRef / detachAiRef / attachAiCanvas from a page, a drop handler
    // or a chip's own remove. Without this key the bar never re-derives and a
    // dropped ref renders no chip at all until some other input happens to
    // tick — the exact class of bug the `_cvSelectionTick` note above records.
    'aiAttached'
  ],
  scope: {
    awareness(el, s) {
      try {
        const out = el.call('buildAiAwareness', s.root, s)
        return out && typeof out === 'object' ? out : { scope: null, focus: [], attached: [] }
      } catch (_) {
        return { scope: null, focus: [], attached: [] }
      }
    },
    // The bar shows when there is something beyond the bare route to show, or
    // while the user is composing (then the scope line + route answer "what
    // will this prompt be about?" before a keystroke).
    //
    // Focus-gated mounts (`focusGated: true` — the global composer's
    // PromptBand; 2026-08-26 per Nika "hide path thing above chat prompt
    // area if textarea is not focused"): while the textarea is NOT focused
    // the bar carries ONLY user-attached items — the scope line and the
    // open-record context follow the composer's attention instead of
    // standing over the page. Ungated mounts (the assistant drawer's
    // composer) keep the original formula.
    visible(el, s) {
      const a = el.scope.awareness(el, s)
      // An attached-only mount (the composer's strip, DESIGN-COMPOSER-FOCUS-
      // POPOVER-CONTEXT-INSPECT-1) shows what the member HANDED the assistant
      // and nothing else — the context itself lives in the focus panel.
      if (el.attachedOnly) return !!a.attached.length
      const focused = !!(s.root && s.root._promptFocused)
      if (el.focusGated && !focused) return !!a.attached.length
      const beyondRoute = a.focus.some((i) => i.kind !== 'route' && i.kind !== 'rows')
      // A surface where the resolver is not registered (standalone canvas app)
      // yields an empty set — never show an empty bar there.
      return !!(a.attached.length || beyondRoute || (focused && a.scope))
    }
  },
  hide: (el, s) => !el.scope.visible(el, s),
  flow: 'y',
  gap: 'Y',
  minWidth: '0',

  // SCOPE — one line, never a chip. Must render, or the bar reads as a fence.
  // On a focus-gated mount the line follows the textarea's focus: the bar can
  // stay visible for attached items while blurred, but the path line itself
  // never stands over the page unfocused.
  ScopeLine: {
    tag: 'p',
    if: (el, s) => !el.parent?.attachedOnly && (!el.parent?.focusGated || !!s.root?._promptFocused),
    margin: '0',
    fontSize: 'X',
    color: 'caption',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '0',
    // The sentence is ONE key with a `{scope}` hole — the same grammar as the
    // chip titles above — so a translation orders "AI can reach" and the line
    // its own way. The line carries tenant content (a workspace name), so the
    // hole is filled by a FUNCTION: a bare string would have `$&` / `$1` in a
    // name read as replacement patterns.
    text: (el, s) => {
      const a = el.scope.awareness(el, s)
      return a.scope && a.scope.line
        ? el.call('polyglot', 'ai.contextBar.scopeLine').replace('{scope}', () => a.scope.line)
        : ''
    }
  },

  // FOCUS then ATTACHED, one row, one grammar. `tier` rides each item so the
  // chip can pick its wash without knowing which list it came from. On a
  // focus-gated mount the FOCUS tier (open-record context) follows the
  // textarea's focus like the scope line — only ATTACHED items (state the
  // user made) survive a blur.
  Chips: {
    flow: 'x',
    flexWrap: 'wrap',
    gap: 'Z',
    minWidth: '0',
    if: (el, s) => {
      const a = el.scope.awareness(el, s)
      const blurredGate =
        el.parent?.attachedOnly || (el.parent?.focusGated && !s.root?._promptFocused)
      return !!((blurredGate ? 0 : a.focus.length) || a.attached.length)
    },
    children: (el, s) => {
      const a = el.scope.awareness(el, s)
      const blurredGate =
        el.parent?.attachedOnly || (el.parent?.focusGated && !s.root?._promptFocused)
      const focusItems = blurredGate ? [] : a.focus
      return [
        ...focusItems.map((i) => ({ ...i, key: i.id, tier: 'focus' })),
        ...a.attached.map((i) => ({ ...i, key: i.id, tier: 'attached' }))
      ]
    },
    childExtends: 'AiContextChip',
    childrenAs: 'state'
  }
}
